import { BRAND_DEFAULTS, type BrandFont } from '@up2d8/contracts';

/**
 * De lettertypen van een huisstijl laden (MRK-2, ADR-0084).
 *
 * Zelf gehost, niet via de CDN van Google: om dezelfde reden als Poppins en Montserrat in
 * `packages/ui/src/styles/base.css` (AVG, en een werkvloer zonder internet). Die twee staan daar
 * statisch en vooraf geladen, en blijven dat: zonder huisstijl verandert er niets.
 *
 * De andere komen pas als iemand ze kiest. Elk lettertype is een eigen stuk van de bundel
 * (`import()` per familie), zodat wie Inter kiest niet ook Merriweather downloadt. Alleen latin en
 * latin-ext - genoeg voor het Nederlands, het Frans en het Engels (é, ë, ç, œ) - en alleen de
 * gewichten die de app gebruikt (400, 500, 600, 700; Lato heeft geen 500 en 600, de browser neemt
 * dan het dichtstbijzijnde).
 *
 * Via de `FontFace`-API en niet met een CSS-bestand van Fontsource: dat bestand bevat ook cyrillisch,
 * grieks en vietnamees, en de bestanden per subset hebben geen `unicode-range`, zodat latin-ext dan
 * latin zou overschrijven. Hier staat de `unicode-range` erbij, en de browser haalt een bestand pas
 * op als er een teken op het scherm staat dat erin valt.
 */

/** Eén gewicht van een lettertype, als URL's naar de bestanden in de bundel. */
export interface BrandFontFile {
  weight: number;
  latin: string;
  latinExt: string;
}

/** De bereiken van Google Fonts, zoals ze in elk CSS-bestand van Fontsource staan. */
const LATIN_RANGE =
  'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD';
const LATIN_EXT_RANGE =
  'U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF';

type LazyFont = Exclude<BrandFont, 'Poppins' | 'Montserrat'>;

/** Eén `import()` per familie: Vite maakt er een eigen stuk van. */
const loaders: Record<LazyFont, () => Promise<{ default: BrandFontFile[] }>> = {
  Inter: () => import('./fonts/inter'),
  Roboto: () => import('./fonts/roboto'),
  'Open Sans': () => import('./fonts/open-sans'),
  Lato: () => import('./fonts/lato'),
  Nunito: () => import('./fonts/nunito'),
  'Source Sans 3': () => import('./fonts/source-sans-3'),
  'Work Sans': () => import('./fonts/work-sans'),
  Merriweather: () => import('./fonts/merriweather'),
};

/** De lettertypen met schreef: die vallen tijdens het laden terug op een schreefletter. */
const SERIF: ReadonlySet<BrandFont> = new Set<BrandFont>(['Merriweather']);

const SANS_FALLBACK = "system-ui, -apple-system, 'Segoe UI', sans-serif";
const SERIF_FALLBACK = "Georgia, 'Times New Roman', serif";

/**
 * De waarde voor `--font-heading` of `--font-body`: de familie met de terugvallers.
 *
 * Voor de standaard precies wat er in tokens.css staat, zodat er niets verschuift.
 */
export function brandFontStack(family: BrandFont): string {
  return `'${family}', ${SERIF.has(family) ? SERIF_FALLBACK : SANS_FALLBACK}`;
}

const pending = new Map<BrandFont, Promise<void>>();

/**
 * Zorgt dat een lettertype uit de vaste lijst geladen kan worden.
 *
 * Idempotent en goedkoop: de tweede aanroep voor dezelfde familie geeft dezelfde belofte terug.
 * Poppins en Montserrat zijn er al (base.css). Voor het voorbeeld op het scherm Huisstijl: roep dit
 * aan voor je het lettertype op een element zet.
 *
 * Een fout (geen netwerk, geen `FontFace` in een oude browser) wordt ingeslikt: dan valt de tekst
 * terug op de systeemletter, en dat is geen reden om een scherm te breken.
 */
export function ensureBrandFont(family: BrandFont): Promise<void> {
  if (family === BRAND_DEFAULTS.fontHeading || family === BRAND_DEFAULTS.fontBody) {
    return Promise.resolve();
  }
  const loader = loaders[family as LazyFont] as (typeof loaders)[LazyFont] | undefined;
  if (!loader || typeof document === 'undefined' || typeof FontFace === 'undefined') {
    return Promise.resolve();
  }

  const known = pending.get(family);
  if (known) return known;

  const promise = loader()
    .then(({ default: files }) => {
      for (const file of files) {
        for (const [url, unicodeRange] of [
          [file.latin, LATIN_RANGE],
          [file.latinExt, LATIN_EXT_RANGE],
        ] as const) {
          document.fonts.add(
            new FontFace(family, `url(${url}) format('woff2')`, {
              weight: String(file.weight),
              style: 'normal',
              display: 'swap',
              unicodeRange,
            }),
          );
        }
      }
    })
    .catch(() => {
      // Volgende keer opnieuw proberen, bv. als het netwerk terug is.
      pending.delete(family);
    });
  pending.set(family, promise);
  return promise;
}
